/**
 * Turning an exported email page into a message body.
 *
 * Unlayer exports a whole page: a doctype, a `<head>` with a `<style>` block, and a `<body>`. The server
 * puts the message body into the organisation's item-30 email as `<p>~~{MESSAGETEXT}~~</p>`, and the
 * inbox and the app show it through `innerHTML`, so a page there goes wrong three ways:
 * - it ends up inside another email, and V2 puts the footer and the compliance block before its own
 *   `</body>`, which is above the end of the item-30 email;
 * - its rules for `body`, `*`, `table` and `td` restyle the item-30 text around it;
 * - in the inbox they restyle the whole app while the message is open.
 *
 * So a page is sent as what an ordinary rich-text editor would send: the body's content, with its styles
 * kept but confined to a wrapper. Rules for Unlayer's own classes (`.u-row`, `.u-col` and the `@media`
 * rules that stack columns on a phone) keep working, and nothing reaches beyond the message.
 *
 * Except in Gmail (web and apps), which ignores a `<style>` in the body: there multi-column rows keep their
 * inline widths and do not stack. The item-30 email has no head to move the block into.
 *
 * Ported to C# as SchoolsBuddy/SchoolsBuddy.Core/Communications/Templating/EmailFragment.cs for Preview and the
 * test email (SB-17220); EmailFragmentTests ports the isHtmlDocument/scopeCss/toEmailFragment cases of this spec
 * one for one, so change both together.
 */

/** Class of the element that wraps a converted page. Every rule of the page's own CSS is confined to it. */
export const EMAIL_BODY_CLASS = 'sb-email-body';

/** True when the HTML is a whole page rather than a fragment. */
export const isHtmlDocument = (html: string): boolean => /<!doctype|<html[\s>]|<body[\s>]/i.test(html ?? '');

/**
 * Splits CSS into its top-level parts: rules (`prelude { block }`) and statements (`@import …;`).
 * Braces and semicolons inside strings, and comments, do not count.
 */
const splitCssParts = (css: string): { prelude: string; block: string | null }[] => {
  const parts: { prelude: string; block: string | null }[] = [];
  let i = 0;

  while (i < css.length) {
    let prelude = '';
    let quote: string | null = null;

    // Up to the `{` that opens a block, or the `;` that ends a statement.
    while (i < css.length) {
      const ch = css[i];
      if (quote) {
        prelude += ch;
        if (ch === '\\') {
          prelude += css[++i] ?? '';
        } else if (ch === quote) {
          quote = null;
        }
        i++;
        continue;
      }
      if (ch === '"' || ch === "'") {
        quote = ch;
      } else if (ch === '{' || ch === ';') {
        break;
      }
      prelude += ch;
      i++;
    }

    if (i >= css.length || css[i] === ';') {
      i++;
      if (prelude.trim()) {
        parts.push({ prelude: prelude.trim(), block: null });
      }
      continue;
    }

    // The block runs to the matching `}`.
    i++;
    let depth = 1;
    let block = '';
    quote = null;
    while (i < css.length && depth > 0) {
      const ch = css[i];
      if (quote) {
        if (ch === '\\') {
          block += ch + (css[++i] ?? '');
          i++;
          continue;
        }
        if (ch === quote) {
          quote = null;
        }
      } else if (ch === '"' || ch === "'") {
        quote = ch;
      } else if (ch === '{') {
        depth++;
      } else if (ch === '}') {
        depth--;
        if (depth === 0) {
          break;
        }
      }
      block += ch;
      i++;
    }
    i++;
    parts.push({ prelude: prelude.trim(), block });
  }

  return parts;
};

/** Splits a selector list on its top-level commas, not those inside `:not(…)`, `[…]` or strings. */
const splitSelectors = (selectorList: string): string[] => {
  const selectors: string[] = [];
  let current = '';
  let depth = 0;
  let quote: string | null = null;

  for (const ch of selectorList) {
    if (quote) {
      if (ch === quote) {
        quote = null;
      }
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === '(' || ch === '[') {
      depth++;
    } else if (ch === ')' || ch === ']') {
      depth--;
    } else if (ch === ',' && depth === 0) {
      selectors.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }

  selectors.push(current.trim());
  return selectors.filter(Boolean);
};

/**
 * Confines one selector to the wrapper. The page's `html` and `body` become the wrapper itself, which
 * carries the body's classes and style, so `body { … }` and `body.u_body { … }` still match.
 */
const scopeSelector = (selector: string, scope: string): string => {
  const withoutRoot = selector.replace(/^(?::root|html)(?![\w-])\s*>?\s*/i, '');

  if (/^body(?![\w-])/i.test(withoutRoot)) {
    return scope + withoutRoot.slice(4);
  }

  return withoutRoot ? `${scope} ${withoutRoot}` : scope;
};

/** At-rules whose blocks hold more rules, which are confined in turn. */
const GROUPING_AT_RULE = /^@(?:-\w+-)?(?:media|supports|container|layer|document)\b/i;

/**
 * Confines every rule of the CSS to elements inside `.{@link EMAIL_BODY_CLASS}`, inside `@media` and
 * similar blocks too. Declarations are kept verbatim, vendor and Outlook properties included. Other
 * at-rules (`@font-face`, `@keyframes`, `@import`) are kept as they are: they style nothing by themselves.
 */
export const scopeCss = (css: string, scope = `.${EMAIL_BODY_CLASS}`): string => {
  const withoutComments = (css ?? '').replace(/\/\*[\s\S]*?\*\//g, '');

  return splitCssParts(withoutComments)
    .map(({ prelude, block }) => {
      if (block === null) {
        return /^@charset\b/i.test(prelude) ? '' : `${prelude};`;
      }
      if (GROUPING_AT_RULE.test(prelude)) {
        return `${prelude} { ${scopeCss(block, scope)} }`;
      }
      if (prelude.startsWith('@')) {
        return `${prelude} { ${block.trim()} }`;
      }

      const selectors = splitSelectors(prelude).map(selector => scopeSelector(selector, scope));
      return selectors.length ? `${selectors.join(', ')} { ${block.trim()} }` : '';
    })
    .filter(Boolean)
    .join('\n');
};

const escapeAttribute = (value: string): string => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');

/**
 * The body of an email page as a fragment, the way an ordinary rich-text editor would send it: the page's
 * `<style>` rules confined to a wrapper that carries the body's classes and style, then the body's content.
 * A fragment is returned unchanged.
 *
 * Kept: the body's markup, Outlook's conditional comments inside it, and web fonts the page links to (as
 * `@import`). Dropped: the doctype, `<title>`, `<meta>`, and whatever else the head holds, such as the
 * Outlook-only settings Unlayer puts there inside a conditional comment.
 */
export const toEmailFragment = (html: string): string => {
  if (!isHtmlDocument(html)) {
    return html;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');

  const fontImports = Array.from(doc.head.querySelectorAll<HTMLLinkElement>('link[rel~="stylesheet"][href]'))
    .map(link => link.getAttribute('href'))
    .filter(href => /^https:\/\//i.test(href))
    .map(href => `@import url("${href.replace(/"/g, '%22')}");`);

  // Styles in the body are confined where they stand; those in the head move into the fragment.
  doc.body.querySelectorAll('style').forEach(style => (style.textContent = scopeCss(style.textContent)));
  const headCss = Array.from(doc.head.querySelectorAll('style'))
    .map(style => scopeCss(style.textContent))
    .filter(Boolean);
  const css = [...fontImports, ...headCss].join('\n');

  const classes = [EMAIL_BODY_CLASS, ...Array.from(doc.body.classList)].join(' ');
  const bodyStyle = doc.body.getAttribute('style');
  const styleAttribute = bodyStyle ? ` style="${escapeAttribute(bodyStyle)}"` : '';

  return `${css ? `<style type="text/css">\n${css}\n</style>\n` : ''}`
    + `<div class="${escapeAttribute(classes)}"${styleAttribute}>${doc.body.innerHTML.trim()}</div>`;
};

/** Name of the container that a message's layout follows in the app: a box around the message. */
const EMAIL_CONTAINER = 'sb-email';

/** `(min-width: …)`, `(max-width: …)` or `(width: …)`, but not `(max-device-width: …)`. */
const WIDTH_FEATURE = /\(\s*(?:min-|max-)?width\s*:[^()]+\)/gi;

/**
 * The conditions of a media query that asks about nothing but the width of a screen, such as
 * `only screen and (min-width: 620px)`, as `(min-width: 620px)`; null for any other query.
 *
 * `em` and `rem` become pixels: in a media query they count from the browser's default font size, in a
 * container query from the container's, and the app's root font size is 10px.
 */
const widthOnlyConditions = (prelude: string): string | null => {
  const query = prelude
    .replace(/^@media\b/i, '')
    .trim()
    .replace(/^(?:only\s+)?(?:screen|all)(?:\s+and\b|$)/i, '')
    .trim();
  const features = query.match(WIDTH_FEATURE);
  // Nothing else may be left: no other feature or media type, no `not`, no second query.
  const rest = query.replace(WIDTH_FEATURE, '').replace(/\band\b/gi, '').trim();
  if (!features || rest) {
    return null;
  }

  return features
    .map(feature => feature.replace(/(\d*\.?\d+)\s*r?em\b/gi, (_, size: string) => `${parseFloat(size) * 16}px`))
    .join(' and ');
};

const toContainerQueries = (css: string): string =>
  splitCssParts(css.replace(/\/\*[\s\S]*?\*\//g, ''))
    .map(({ prelude, block }) => {
      if (block === null) {
        return `${prelude};`;
      }

      const conditions = /^@media\b/i.test(prelude) ? widthOnlyConditions(prelude) : null;
      if (!conditions) {
        return `${prelude} { ${block.trim()} }`;
      }

      return `@supports not (container-type: inline-size) { ${prelude} { ${block.trim()} } }\n`
        + `@container ${EMAIL_CONTAINER} ${conditions} { ${block.trim()} }`;
    })
    .join('\n');

/**
 * A sent message as the app shows it: laid out for the width of the pane it is read in, as an email
 * client of that width would, instead of the width of the window.
 *
 * Unlayer fixes its rows at the design's width (600px) for any screen wider than that, and stacks the
 * columns only below it. In a reading pane narrower than the design inside a wide window, the message
 * then scrolls sideways. So each of the message's media queries that asks only about the width becomes a
 * container query, kept as it was for a browser without container queries. What is stored and emailed does
 * not change: email clients know no containers.
 *
 * The container is a box put around the message, declared in its own `style` attribute:
 * - not the wrapper itself, as an element cannot query itself, and the page's `body` rules apply to the
 *   wrapper;
 * - not in a style block, where a rule ahead of the page's web-font `@import` would void the import.
 *
 * Anything that is not a converted page ({@link toEmailFragment}) is returned unchanged.
 */
export const toInAppEmailHtml = (html: string): string => {
  if (!html?.includes(EMAIL_BODY_CLASS)) {
    return html;
  }

  const converted = html.replace(
    /(<style\b[^>]*>)([\s\S]*?)(<\/style>)/gi,
    (_, open: string, css: string, close: string) => `${open}\n${toContainerQueries(css)}\n${close}`
  );
  return `<div style="container: ${EMAIL_CONTAINER} / inline-size">${converted}</div>`;
};

/**
 * True when the HTML shows the reader anything: text other than white space, or an image. A blank
 * Unlayer design still exports a full page of layout tables, so the length of the HTML says nothing.
 */
export const hasVisibleContent = (html: string): boolean => {
  if (!html) {
    return false;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('style, script, title').forEach(element => element.remove());

  return (doc.body.textContent ?? '').trim().length > 0 || !!doc.body.querySelector('img');
};
